import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject, defer, forkJoin, from, map, switchMap, take, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { chainIdToName } from '../../constants/networks';

import { AuthService } from '@services/auth.service';
import { UserApiService } from '@http/user-api.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { ISignatureWallet } from '@http/interfaces/transaction';

import { createAppKit } from '@reown/appkit'
import { EthersAdapter } from '@reown/appkit-adapter-ethers'
import { BrowserProvider, parseEther, ethers, Eip1193Provider } from 'ethers'
import { Contract } from "ethers";

import coindepoAbi from './CoindepoSaleDistribution.abi.json';

import { 
  mainnet,
  sepolia,
  polygon, 
  polygonMumbai, 
  avalanche, 
  avalancheFuji, 
  bsc, 
  bscTestnet 
} from '@reown/appkit/networks';
import { Router } from '@angular/router';

export const mainnets = [
  mainnet,
  bsc,
  polygon,
  avalanche,
];

export const testnets = [
  sepolia,
  polygonMumbai,
  avalancheFuji,
  bscTestnet
];

// Project ID of WalletConnect
const projectId = '4903263b40b9c30dbc295f0509510756';

// Metadata for modal
const metadata = {
    name: 'CoinDepo',
    description: 'CoinDepo - Smart Profit',
    url: 'https://dapp.coindepo.com/',
    icons: ['https://static.coindepo.com/icons/coindepo_eth.svg']
}

@Injectable({
  providedIn: 'root',
})
export class WalletWeb3Service {
  modal: any;
  provider: any;
  walletAddress?: string | null;
  chainID?: number;
  networkName?: string | null;
  isLogged: boolean = false;
  isConnectProccess: boolean = false;

  dispatchState$ = new Subject<void>();
  proccessBuying$ = new BehaviorSubject(false);
  proccessBuyingError$ = new BehaviorSubject(false);

  showToast = false;
  prevChainID: number | undefined = -1;
  signature$ = new BehaviorSubject<ISignatureWallet>({ to: '', from: '', hash: '' })

  tokenContract!: Contract;
  isClaimingLoading = false;

  constructor(
    private userApi: UserApiService,
    private authService: AuthService,
    private toast: ToastrService,
    private translate: TranslateService,
    private router: Router,
  ) {
    this.initWalletWork();
  }

  async initWalletWork() { 
    this.modal = await createAppKit({
      adapters: [
        new EthersAdapter(),
        // Injection Adapter wallets
        // new InjectedAdapter()
      ],
      networks: environment.env === 'dev' || environment.env === 'stage'
      ? [
        sepolia,
        polygonMumbai,
        avalancheFuji,
        bscTestnet 
      ] : [
        mainnet,
        bsc,
        polygon,
        avalanche,
      ],
      metadata,
      projectId,
      features: {
        analytics: false,
        socials: [],
        email: false,
      }
    })

    await this.modal.subscribeProviders((providerList: any) => {
      this.handlerModalActions(providerList['eip155']);
    });

    this.modal.subscribeNetwork((network: any) => {      
      this.chainID = network.chainId;
      this.networkName = chainIdToName[network.chainId];
      this.modal.close();
    });
  }

  handlerModalActions(provider: any) {     
    if (this.modal.getAddress()) {
      this.provider = provider;
      this.chainID = this.modal.getCaipNetworkId();
      // @ts-ignore
      this.networkName = chainIdToName[this.chainID];
      this.walletAddress = this.modal.getAddress();            

      this.dispatchState$.next();
      if (this.provider && this.walletAddress) {

        if (!this.showToast && this.prevChainID !== this.chainID) {
          this.showToast = true;
          this.prevChainID = this.chainID;
        }

        if (!this.authService.isLoggedIn) {
          this.toast.success(this.translate.instant('shared.you-wallet-authed'))
          .onHidden.pipe(take(1)).subscribe(() => this.showToast = false);
          this.authorizeWithWallet()
        } else if (this.modal.getIsConnected && this.modal.getIsConnected())  {
          this.toast.success(
            this.translate.instant('shared.you-wallet-connected', {
              wallet: this.walletAddress,
              network: this.networkName
            })
          );
        }
      } else {
        // @ts-ignore
        this.authService.logout();
      }
    }
  }

  // Work with Wallet
  async connectWallet() {
    await this.modal.open(); 
  }


  async disconnectWallet() {        
    if (this.chainID) {             
      await this.modal.disconnect();
      this.authService.logout();
      this.provider = null;
      this.chainID = undefined;
      this.walletAddress = null;
      this.isLogged = false;      
    }

    // // Hard logout:
    // if (this.chainID) {
    //   this.modal.resetAccount(this.chainID);
    //   this.modal.resetNetwork();
  
    //   if (this.modal.resetWcConnection) {
    //     this.modal.resetWcConnection();
    //   }
  
    //   this.authService.logout();
    //   localStorage.clear();
    //   window.location.reload();
    // }
  }

  chooseNetwork(network?: number) {
    if (network) {
      this.modal.switchNetwork(network)
    } else {
      this.modal.open({view: 'Networks'})
    }
  }

  // Auth
  authorizeWithWallet(): any {
    if (!this.provider || !this.walletAddress || this.authService.isLoggedIn) {      
      return from('')
    }

    this.userApi.verifyAddress(this.walletAddress).pipe(
      switchMap((res: any) => {
        return from(this.provider.request({
          method: 'personal_sign',
          params: [res.nonce, this.walletAddress],
        }));
      }),
      // @ts-ignore
      switchMap((res) => {

        if (this.walletAddress) {          
          return this.authService.authViaSignature(this.walletAddress, res as string)
        }
        
      }),
      tap(() => {
        this.toast.success(
          this.translate.instant('shared.you-wallet-connected', {wallet: this.walletAddress, network: this.networkName})
        )
        this.router.navigate(['/'])
      })
    ).subscribe()
  }

  // Work with transactions
  async createAndSignTransaction({
    from, to, amount, chainId, gas, gasPrice, nonce, value, data
  }: any): Promise<any> {
    if (!this.provider || !this.walletAddress) {
      return;
    }

    const transactionConfig: any = {
      from,
      to,      
      value: parseEther(amount.toString()),
      gas,
      gasPrice,
      nonce,
      chainId
    };
  
    if(data) {
      transactionConfig.value = value
      transactionConfig.data = data
    }
  
    try {      
      const ethersProvider = new BrowserProvider(this.provider);
      const signer = await ethersProvider.getSigner();
      const transaction = await signer.sendTransaction(transactionConfig);    

      return {...transaction, error: null}
    } catch (error) {
      console.log('Transaction Error:', error);
      this.proccessBuyingError$.next(true);
      return {transaction: null, error}
    }
  }
  
  // Working with blockchain
  loadCoindepoContractData(rate = '0.100000'): Observable<any> {
    if (this.walletAddress) {
      const address = this.walletAddress;

      return defer(async () => {
        const rawProvider = await this.modal.getProvider("eip155");
        const provider = new BrowserProvider(rawProvider);
        const tokenAddress = environment.contract;
        const tokenContract = new Contract(tokenAddress, coindepoAbi, provider);

        return forkJoin({
          getAvailableUserClaimAmount: defer(() => tokenContract['getAvailableUserClaimAmount'](address)),
          getVestingDataByUser: defer(() => tokenContract['getVestingDataByUser'](address)),
        });
      }).pipe(
        switchMap(obs => obs),
        map(result => {
          const decimals = 18;
          const withdrawn = Number(ethers.formatUnits(result.getVestingDataByUser[4], decimals));
          const withdrawnUSD = withdrawn * Number(rate);
          const amount = Number(ethers.formatUnits(result.getVestingDataByUser[0], decimals)) - withdrawn;
          const amountUSD = amount * Number(rate);
          const availableUserClaimAmount = Number(ethers.formatUnits(result.getAvailableUserClaimAmount, decimals));
          const availableUserClaimAmountUSD = availableUserClaimAmount * Number(rate);
          const locked = amount - availableUserClaimAmount;
          const lockedUSD = locked * Number(rate);
          
          return {
            amount,
            amountUSD,
            availableUserClaimAmount,
            availableUserClaimAmountUSD,
            locked,
            lockedUSD,
            withdrawn,
            withdrawnUSD
          };
        })
      );
    } else {
      return this.dispatchState$.pipe(
        take(1),
        switchMap(() => this.loadCoindepoContractData(rate))
      );
    }
  }

  async claimTokens(appKit: any = this.modal): Promise<any> {
    const rawProvider = await appKit.getProvider('eip155');
    if (!rawProvider) this.toast.error("Provider doesn't exist!");
    if (!this.walletAddress) this.toast.error("Wallet doesn't connect!");
    
    this.isClaimingLoading = true;
    const ethersProvider = new BrowserProvider(rawProvider);
    const signer = await ethersProvider.getSigner();

    const tokenAddress = environment.contract;
    const tokenContract = new Contract(tokenAddress, coindepoAbi, signer);
  
    const tx = await tokenContract['claimVesting']();
    const receipt = await tx.wait();
    this.isClaimingLoading = false;

    return receipt;
  }

  get isWalletConnected() {
    if (!this.modal.getIsConnected()) {
      this.provider = null;
      this.chainID = undefined;
      this.walletAddress = null;
      this.isLogged = false;
      this.authService.logout()
    }

    return this.modal.getIsConnected()
  }
}
